import { useCallback } from 'react';
import { FiTrash } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import deleteCompanyApi from '@/api/delete/company/delete-company';
import getCompanyApi from '@/api/get/company/get-company';
import theme from '@/common/theme';
import Alert from '@/components/alert/alert-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import Card from '@/components/card/card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Pill from '@/components/pill/pill-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import {
  BodyText,
  Heading3Text,
  SmallTitle,
  Subtitle2Text,
} from '@/utils/global-fonts';
import { toSentenceCase } from '@/utils/utils';
import Styled from './company-view-style';

const styledActions = {
  paddingTop: '24px',
  paddingBottom: '24px',
  paddingLeft: '4px',
  paddingRight: '4px',
  display: 'flex',
  justifyContent: 'space-between',
};

/**
 * Company view component
 * @returns {JSX.Element}
 */
const CompanyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useQuery(getCompanyApi, id);
  const { company, whiteLabelling, suppliers } = data ?? {};
  const [deleteCompany, { loading: deleteCompanyLoading }] =
    useMutation(deleteCompanyApi);

  const launchDeleteConfirmation = useCallback(() => {
    ConfirmationModal({
      title: 'Delete Company',
      text: `Are you sure you want to delete ${company?.name}?`,
      onConfirm: () =>
        deleteCompany(id).then(() => {
          navigate('/app/company-management');
          Alert({
            icon: 'success',
            text: 'Company deleted successfully',
          });
        }),
    });
  }, [company, deleteCompany, id, navigate]);

  const quoteAccess = company?.quoting_function_access;
  const salesPortalAccess = company?.sales_portal_access;
  const dealershipAccess = company?.dealership_access;

  if (!deleteCompanyLoading && !loading) {
    return (
      <Styled.Main>
        <Col style={Styled.mainStyledCol} xs={12}>
          <Row>
            <Col xs={12} md={12} lg={9}>
              <Card padding="16px 18px 10px 24px">
                <Row align="center" $margin={0}>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <Heading3Text>Details</Heading3Text>
                  </Styled.DivStyle>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <Row $margin={0} justify="end">
                      <StatusIndicator status={company?.status} />
                    </Row>
                  </Styled.DivStyle>
                </Row>
                <Row align="center">
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <SmallTitle>COMPANY NAME</SmallTitle>
                    <BodyText>{toSentenceCase(company?.name)}</BodyText>
                  </Styled.DivStyle>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <SmallTitle>REGISTERED ADDRESS</SmallTitle>
                    <BodyText>{toSentenceCase(company?.address)}</BodyText>
                  </Styled.DivStyle>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <SmallTitle>EMAIL ADDRESS</SmallTitle>
                    <BodyText>{company?.email}</BodyText>
                  </Styled.DivStyle>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <SmallTitle>REGISTRATION NUMBER</SmallTitle>
                    <BodyText>{company?.registration_number}</BodyText>
                  </Styled.DivStyle>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <SmallTitle>PHONE NUMBER</SmallTitle>
                    <Styled.PhoneNumberContainer>
                      <Styled.PhoneInput
                        name="contact_number"
                        disabled
                        value={company?.contact_number}
                      />
                      <BodyText>{company?.contact_number}</BodyText>
                    </Styled.PhoneNumberContainer>
                  </Styled.DivStyle>
                  <Styled.DivStyle sm={12} md={12} lg={6} xl={6} xxl={6}>
                    <SmallTitle>UNREAL ENGINE INTEGRATION</SmallTitle>
                    <Styled.PhoneNumberContainer>
                      <BodyText>
                        {company?.ue_endpoint || 'ETTX Default'}
                      </BodyText>
                    </Styled.PhoneNumberContainer>
                  </Styled.DivStyle>
                  {suppliers.length > 0 && (
                    <Col md={12} lg={12}>
                      <Heading3Text>Suppliers</Heading3Text>
                      <Styled.SuppliersContainer>
                        <Row>
                          {suppliers?.map((supplier, i) => (
                            <Pill
                              key={`suppliers___supplier______${i + 1}`}
                              style={Styled.styledPill}
                              label={supplier.name}
                            />
                          ))}
                        </Row>
                      </Styled.SuppliersContainer>
                    </Col>
                  )}
                  <Col md={12} lg={12}>
                    {(dealershipAccess || quoteAccess || salesPortalAccess) && (
                      <Heading3Text>Access</Heading3Text>
                    )}
                    <Row>
                      <Col md={12} lg={12} $margin="16px 0px 0px 0px">
                        {salesPortalAccess && (
                          <Styled.CheckboxContainer>
                            <Styled.Checkbox>
                              <Styled.CheckMark />
                            </Styled.Checkbox>
                            <Styled.CheckboxLabel>
                              Access to Sales Portal
                            </Styled.CheckboxLabel>
                          </Styled.CheckboxContainer>
                        )}
                      </Col>
                      <Col md={6} lg={6}>
                        {quoteAccess && (
                          <Styled.CheckboxContainer>
                            <Styled.Checkbox>
                              <Styled.CheckMark />
                            </Styled.Checkbox>
                            <Styled.CheckboxLabel>
                              Quoting Function Activated
                            </Styled.CheckboxLabel>
                          </Styled.CheckboxContainer>
                        )}
                      </Col>
                      <Col md={6} lg={6}>
                        {dealershipAccess && (
                          <Styled.CheckboxContainer>
                            <Styled.Checkbox>
                              <Styled.CheckMark />
                            </Styled.Checkbox>
                            <Styled.CheckboxLabel>
                              Access granted to dealership
                            </Styled.CheckboxLabel>
                          </Styled.CheckboxContainer>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={12} md={12} lg={3}>
              <Card padding="">
                <Styled.ActionsContainer>
                  <Subtitle2Text>Actions</Subtitle2Text>
                  <Styled.CompanyColors>
                    <Styled.CompanyPrimary
                      $color={
                        whiteLabelling?.primary_color ?? theme.COLOR_PRIMARY
                      }
                    />
                    <Styled.CompanySecondary
                      $color={
                        whiteLabelling?.secondary_color ?? theme.COLOR_SECONDARY
                      }
                    />
                  </Styled.CompanyColors>
                </Styled.ActionsContainer>
                {whiteLabelling?.logo && (
                  <Styled.LogoContainer>
                    <Styled.CompanyLogo src={whiteLabelling.logo} alt="logo" />
                  </Styled.LogoContainer>
                )}
                <Col
                  style={styledActions}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  xxl={12}
                >
                  <PermissionWrapper
                    permissions={['option.create', 'option.update']}
                    satisfyAll={false}
                  >
                    <PrimaryOutlineButton
                      buttonWidth="108px"
                      type="Button"
                      title="Delete"
                      onClick={() => {
                        launchDeleteConfirmation();
                      }}
                      icon={<FiTrash />}
                      margin="0 0 0 0"
                    />
                    <PrimaryButton
                      buttonWidth="108px"
                      type="submit"
                      title="Edit"
                      onClick={() => {
                        navigate(`/app/company-management/edit/${id}`);
                      }}
                    />
                  </PermissionWrapper>
                </Col>
              </Card>
            </Col>
          </Row>
          {whiteLabelling && (
            <Row>
              <Col xs={12} md={12} lg={9}>
                <Card padding="16px 18px 10px 24px">
                  <Subtitle2Text>Terms & Conditions</Subtitle2Text>
                  <Styled.LinkContainer>
                    <SmallTitle>
                      {whiteLabelling?.terms_type === 'text' ? 'TEXT' : 'LINK'}
                    </SmallTitle>
                    {whiteLabelling?.terms_type === 'text' ? (
                      <BodyText>{whiteLabelling?.terms_content}</BodyText>
                    ) : (
                      <Link to={whiteLabelling?.terms_content} target="__blank">
                        {whiteLabelling?.terms_content}
                      </Link>
                    )}
                  </Styled.LinkContainer>
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Styled.Main>
    );
  }
  return (
    <Styled.Div>
      <Loader loading />
    </Styled.Div>
  );
};

CompanyView.defaultProps = {};

CompanyView.propTypes = {};

export default CompanyView;
